import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Radio } from 'react-bootstrap';
import { Grid, Column } from 'components/Layout';
import { get, map } from 'lodash';
import {
  nextAction,
  checkPreviousAction,
} from 'actions/workflow';
import cn from 'classnames';
import { PuffLoader } from 'react-spinners';

import Header from 'components/Header';
import Footer from 'components/Footer';
import { Button } from 'components/Button';
import User from 'assets/images/pending.svg';
import style from './style.scss';

const IdCheckQuestions = () => {
  const workflow = useSelector((state) => state.workflow);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [request, setRequest] = useState({});
  const [isAllFilled, setIsAllFilled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const questions = useMemo(() => get(workflow, 'data.questions'), [workflow]);
  const attempt = useMemo(() => get(workflow, 'data.attempt'), [workflow]);
  const key = useMemo(() => get(searchParams, 'key'), [searchParams]);

  const initializeRequest = (workflowArg) => {
    const { questions: questionArg, conversationId, questionSetId } = get(workflowArg, 'data');
    const requestTmp = {
      ConversationId: conversationId,
      QuestionSetId: questionSetId,
    };
    const answers = questionArg.map((q) => {
      const { questionId } = q;
      return {
        QuestionId: questionId,
        ChoiceId: null,
      };
    });
    requestTmp.Answers = answers;
    setRequest(requestTmp);
  };

  useEffect(() => {
    if (!key) {
      navigate(`/${params.workflowtype}/application`);
    } else {
      setIsQuestionsLoading(true);
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/fsl/${key}/checkin`,
        success: (res) => {
          setIsQuestionsLoading(false);
          initializeRequest(res);
        },
        fail: () => {
          setIsQuestionsLoading(false);
          navigate(`/fsl/autopayelection?key=${key}`, { replace: true});
        },
      }));
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleMerchantReturnClick = (e) => {
    e.preventDefault();
    if (key) {
      navigate('/dashboard');
    }
  };

  const handleChange = (event, choiceId) => {
    const { Answers } = request;
    let answersToUpdate = map(Answers, answer => answer);
    answersToUpdate = answersToUpdate.map((answer) => {
      if (answer.QuestionId === event.target.name) {
        return {
          ...answer,
          ChoiceId: choiceId,
        };
      }
      return answer;
    });
    const emptyAnswers = answersToUpdate.filter(answer => answer.ChoiceId === null) || [];
    if (Array.isArray(emptyAnswers) && emptyAnswers.length === 0 && Array.isArray(Answers) && Answers.length > 0) {
      setIsAllFilled(true);
    }
    setRequest({
      ...request,
      Answers: answersToUpdate,
    });
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    if (!isAllFilled) setIsValid(false);
    else setIsValid(true);

    if (key && isAllFilled) {
      setIsLoading(true);
      dispatch(nextAction({
        data: request,
        url: `/workflows/fsl/${key}/next`,
        success: (res) => {
          const routeUrl = res.data && res.data.url;
          setIsAllFilled(false);
          setIsLoading(false);
          const workflowTmp = res.data.questions && res;
          if (workflowTmp) initializeRequest(workflowTmp);
          navigate(routeUrl);
        },
        fail: (error) => {
          setIsAllFilled(false);
          setIsLoading(false);
          navigate(`/${params.workflowtype}/general-error-page`, {
            state: {
              data: error.data,
            },
          });
        },
      }));
    }
  };

  return (
    <Fragment>
      <Header />
      <div className="grid-container fluid">
        <Grid className="grid-margin-x max-limited">
          <Column className={style['mt-30']}>
            <h2 className={style['kba-heading']}>Identity Verification</h2>
            <div className={cn(style['d-flex'], style['align-items-center'])}>
              <div>
                <img src={User} alt="User Icon" />
              </div>
              <div className={style['pl-30']}>
                <div>LendingUSA</div>
              </div>
            </div>
          </Column>
        </Grid>
        <Grid className="grid-margin-x max-limited">
          <Column sm={{ size: 8, offset: 2 }} className={style['pt-20']}>
            {!isLoading &&
              attempt === 2 && (
                <div className={style['pt-10']}>
                  <p className={style['form-note']}>Sorry, we were unable to confirm your identity. Please try again.</p>
                </div>
              )}
            <h4>ID Check - Identification Questions</h4>
            <p>Please respond to the questions below so that we may verify your identity. Once your identity has been verified, you&#39;ll be able to sign your loan documents.</p>
          </Column>
        </Grid>
        <Grid className="grid-margin-x max-limited">
          {isQuestionsLoading && (
            <div className={style.loadingContainer}>
              <PuffLoader
                color="#f44242"
                size={100}
              />
            </div>
          )}
          {!isQuestionsLoading && (
            <Column>
              {questions &&
                questions.map((q) => {
                  const { choices, question, questionId } = q;
                  return (
                    <div className={style['pt-05']} key={questionId}>
                      <div sm={12}>
                        <div>
                          <strong>{question}</strong>
                        </div>
                        <div className={cn(style['d-flex'], style['flex-wrap'])}>
                          {
                            choices.map(c => (
                              <div className={style['half-width']} key={c.choiceId}>
                                <Radio value="Answer1" className={style.radio} name={questionId} onChange={e => handleChange(e, c.choiceId)}>{c.choice}</Radio>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  );
                })}
              {!isValid && (
                <div className={style['pt-10']}>
                  <p className={style['form-note']}>Please answer all of the questions provided.</p>
                </div>
              )}
            </Column>
          )}
        </Grid>
        <Grid className="grid-margin-x max-limited">
          <Column className={cn('d-flex justify-content-center', style['pt-30'])}>
            <Button
              color="primary"
              className="button large green"
              onClick={handleSubmitForm}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Submit
            </Button>
            <Button
              className={cn('button large', style['ml-30'])}
              onClick={handleMerchantReturnClick}
              color="primary"
            >
              Cancel
            </Button>
          </Column>
        </Grid>
      </div>
      <br />
      <Footer />
    </Fragment>
  );
};

export default IdCheckQuestions;
